/**
 * stateDetection.js - Comprehensive State Detection Engine
 * Baba AI - Full Spectrum Neurodivergent + Neurotypical Mapping
 * 
 * PHILOSOPHY:
 * - Detect, don't ask
 * - Adapt, don't label
 * - Support, don't diagnose
 * - "Bedoel je dat?" when uncertain
 * 
 * SPECTRUM COVERAGE:
 * - ADHD patterns (hyperactive, inattentive, combined)
 * - Autism spectrum patterns (sensory, routine, social)
 * - Bipolar patterns (manic, depressive, mixed)
 * - Psychosis indicators (for safety, not diagnosis)
 * - Neurotypical patterns (casual to power user)
 * 
 * CRITICAL: This is for ADAPTATION, not medical diagnosis.
 * All data stays LOCAL. No external transmission.
 * 
 * Updated: November 26, 2025
 */

class StateDetection {
  constructor() {
    this.storageKey = 'baba-state-detection';
    this.sessionData = [];
    this.currentState = this.loadState();
    this.patternWindow = []; // Rolling window for pattern detection
    this.WINDOW_SIZE = 20; // Messages to analyze
  }

  // ==========================================
  // STATE STRUCTURE
  // ==========================================

  getDefaultState() {
    return {
      // Current detected profile
      profile: {
        primary: 'unknown',      // Most likely profile
        confidence: 0,           // 0-1 confidence
        traits: [],              // Active traits detected
        lastUpdated: null
      },

      // ADHD Indicators
      adhd: {
        score: 0,                // 0-100
        subtype: null,           // hyperactive, inattentive, combined
        indicators: {
          hyperfocus: 0,         // Extended deep sessions
          taskSwitching: 0,      // Rapid topic changes
          impulsivity: 0,        // Quick responses, interruptions
          timeBlindness: 0,      // Late night sessions, losing track
          paralysis: 0,          // Long gaps, incomplete thoughts
          activationNeeded: 0    // Needs external push to start
        }
      },

      // Autism Spectrum Indicators
      autism: {
        score: 0,
        indicators: {
          routineSeeking: 0,     // Same time, same patterns
          detailFocus: 0,        // Deep dives, precision requests
          literalLanguage: 0,    // Direct questions, explicit needs
          sensoryMentions: 0,    // References to overwhelm
          specialInterest: 0,    // Intense topic focus
          socialScripting: 0     // Formulaic greetings, structured requests
        }
      },

      // Bipolar Indicators
      bipolar: {
        score: 0,
        currentPhase: null,      // manic, depressive, mixed, stable
        indicators: {
          elevated: {
            grandiosePlans: 0,   // Huge ambitions, rapid fire
            decreasedSleep: 0,   // Late night productivity
            pressuredSpeech: 0,  // Long rapid messages
            riskTaking: 0,       // Impulsive decisions
            irritability: 0      // Frustration, impatience
          },
          depressed: {
            lowEnergy: 0,        // Short messages, long gaps
            hopelessness: 0,     // Negative self-talk
            indecision: 0,       // Can't choose, seeking validation
            worthlessness: 0,    // Self-deprecation
            withdrawal: 0        // Decreased engagement
          }
        }
      },

      // Psychosis Safety Indicators (for safety checks, not diagnosis)
      psychosis: {
        concernLevel: 0,         // 0-100, triggers safety protocols at high levels
        indicators: {
          disorganizedThought: 0,    // Incoherent, tangential
          persecutory: 0,            // Everyone against me
          grandiose: 0,              // Special powers, chosen one
          referential: 0,            // Hidden messages everywhere
          realityTesting: 0          // Can distinguish real/unreal
        }
      },

      // Neurotypical Spectrum
      neurotypical: {
        score: 0,
        userType: null,          // casual, regular, power, heavy
        indicators: {
          consistentPacing: 0,   // Steady, predictable usage
          linearThinking: 0,    // Topic → topic logically
          socialNorms: 0,       // Standard greetings, politeness
          emotionalRegulation: 0, // Stable tone throughout
          workLifeBalance: 0    // Normal hours, breaks
        }
      },

      // Anxiety/Stress (cross-cutting, affects everyone)
      anxiety: {
        score: 0,
        indicators: {
          reassuranceSeeking: 0, // "Is this right?", validation needs
          catastrophizing: 0,    // Worst case scenarios
          avoidance: 0,          // Delaying, not facing issues
          perfectionism: 0,      // Endless revisions
          physicalMentions: 0    // Mentions of stress symptoms
        }
      },

      // Meta tracking
      meta: {
        totalAnalyzed: 0,
        firstSeen: null,
        lastAnalyzed: null,
        confidenceHistory: []
      }
    };
  }

  // ==========================================
  // CORE ANALYSIS
  // ==========================================

  /**
   * Analyze a message and update state
   * Call this on every user message
   */
  analyzeMessage(message, metadata = {}) {
    if (!message || typeof message !== 'string') return;

    const timestamp = metadata.timestamp || Date.now();
    const messageData = this.extractMessageFeatures(message, timestamp);
    
    // Add to rolling window
    this.patternWindow.push(messageData);
    if (this.patternWindow.length > this.WINDOW_SIZE) {
      this.patternWindow.shift();
    }

    // Update all indicator scores
    this.updateADHDIndicators(messageData);
    this.updateAutismIndicators(messageData);
    this.updateBipolarIndicators(messageData);
    this.updatePsychosisIndicators(messageData);
    this.updateNeurotypicalIndicators(messageData);
    this.updateAnxietyIndicators(messageData);

    // Calculate profile scores
    this.calculateProfileScores();

    // Determine primary profile
    this.determinePrimaryProfile();

    // Update meta
    this.currentState.meta.totalAnalyzed++;
    this.currentState.meta.lastAnalyzed = timestamp;
    if (!this.currentState.meta.firstSeen) {
      this.currentState.meta.firstSeen = timestamp;
    }

    // Save periodically
    if (this.currentState.meta.totalAnalyzed % 5 === 0) {
      this.saveState();
    }

    return this.getCurrentAdaptation();
  }

  /**
   * Extract features from a single message
   */
  extractMessageFeatures(message, timestamp) {
    const words = message.split(/\s+/);
    const sentences = message.split(/[.!?]+/).filter(s => s.trim());
    
    return {
      timestamp,
      length: message.length,
      wordCount: words.length,
      sentenceCount: sentences.length,
      avgWordLength: words.reduce((sum, w) => sum + w.length, 0) / (words.length || 1),
      avgSentenceLength: words.length / (sentences.length || 1),
      
      // Punctuation patterns
      questionMarks: (message.match(/\?/g) || []).length,
      exclamationMarks: (message.match(/!/g) || []).length,
      ellipsis: (message.match(/\.\.\./g) || []).length,
      caps: (message.match(/[A-Z]{2,}/g) || []).length,
      
      // Emotional markers
      hasEmoji: /[\u{1F300}-\u{1F9FF}]/u.test(message),
      hasProfanity: this.detectProfanity(message),
      sentiment: this.quickSentiment(message),
      
      // Structural patterns
      hasList: /^[-*•]\s/m.test(message) || /^\d+\.\s/m.test(message),
      hasCode: /```/.test(message),
      hasUrl: /https?:\/\//.test(message),
      
      // Topic markers
      topics: this.extractTopics(message),
      
      // Time features
      hour: new Date(timestamp).getHours(),
      isWeekend: [0, 6].includes(new Date(timestamp).getDay()),
      
      // Gap from previous (if available)
      gapFromPrevious: this.getGapFromPrevious(timestamp)
    };
  }

  /**
   * Get time gap from previous message
   */
  getGapFromPrevious(timestamp) {
    if (this.patternWindow.length < 2) return null;
    const prev = this.patternWindow[this.patternWindow.length - 2];
    return (timestamp - prev.timestamp) / 1000; // seconds
  }

  // ==========================================
  // INDICATOR UPDATES
  // ==========================================

  /**
   * Update ADHD indicators
   */
  updateADHDIndicators(msg) {
    const adhd = this.currentState.adhd.indicators;
    const alpha = 0.15; // Learning rate

    // Hyperfocus: Long sessions, deep detailed messages
    if (msg.wordCount > 200 || msg.hasCode) {
      adhd.hyperfocus = this.smooth(adhd.hyperfocus, 1, alpha);
    }

    // Task switching: Topic changes in pattern window
    if (this.patternWindow.length >= 3) {
      const topicChanges = this.countTopicChanges();
      if (topicChanges > 2) {
        adhd.taskSwitching = this.smooth(adhd.taskSwitching, 1, alpha);
      }
    }

    // Impulsivity: Quick responses, short messages
    if (msg.gapFromPrevious && msg.gapFromPrevious < 10 && msg.wordCount < 20) {
      adhd.impulsivity = this.smooth(adhd.impulsivity, 1, alpha);
    }

    // Time blindness: Late night usage (23:00 - 04:00)
    if (msg.hour >= 23 || msg.hour < 4) {
      adhd.timeBlindness = this.smooth(adhd.timeBlindness, 1, alpha);
    }

    // Paralysis: Long gaps, incomplete messages
    if (msg.gapFromPrevious && msg.gapFromPrevious > 1800) { // 30+ min gap
      adhd.paralysis = this.smooth(adhd.paralysis, 1, alpha);
    }
    if (msg.ellipsis > 2) {
      adhd.paralysis = this.smooth(adhd.paralysis, 0.5, alpha);
    }

    // Activation needed: Phrases indicating stuck
    const activationPhrases = ['waar was ik', 'help me starten', 'kan niet beginnen', 'stuck', 'blocked'];
    if (activationPhrases.some(p => msg.topics.includes(p))) {
      adhd.activationNeeded = this.smooth(adhd.activationNeeded, 1, alpha);
    }

    // Decay when not showing indicators
    Object.keys(adhd).forEach(key => {
      adhd[key] = Math.max(0, adhd[key] - 0.01);
    });
  }

  /**
   * Update Autism spectrum indicators
   */
  updateAutismIndicators(msg) {
    const autism = this.currentState.autism.indicators;
    const alpha = 0.12;

    // Routine seeking: Same hours, consistent patterns
    if (this.patternWindow.length >= 5) {
      const hours = this.patternWindow.map(m => m.hour);
      const hourVariance = this.variance(hours);
      if (hourVariance < 2) {
        autism.routineSeeking = this.smooth(autism.routineSeeking, 1, alpha);
      }
    }

    // Detail focus: Long precise messages, lists, code
    if (msg.hasList || msg.hasCode || msg.avgSentenceLength > 20) {
      autism.detailFocus = this.smooth(autism.detailFocus, 1, alpha);
    }

    // Literal language: Direct questions, explicit structure
    if (msg.questionMarks > 0 && msg.wordCount < 30) {
      autism.literalLanguage = this.smooth(autism.literalLanguage, 0.5, alpha);
    }

    // Sensory mentions
    const sensoryWords = ['overwhelming', 'loud', 'bright', 'noise', 'overload', 'too much', 'sensory'];
    if (sensoryWords.some(w => msg.topics.includes(w))) {
      autism.sensoryMentions = this.smooth(autism.sensoryMentions, 1, alpha);
    }

    // Special interest: Same topic repeatedly
    if (this.patternWindow.length >= 5) {
      const topicRepetition = this.measureTopicRepetition();
      if (topicRepetition > 0.6) {
        autism.specialInterest = this.smooth(autism.specialInterest, 1, alpha);
      }
    }

    // Social scripting: Formulaic patterns
    const scripts = ['hallo', 'goedemorgen', 'bedankt', 'met vriendelijke groet', 'hi claude'];
    if (scripts.some(s => msg.topics.includes(s))) {
      autism.socialScripting = this.smooth(autism.socialScripting, 0.3, alpha);
    }

    // Decay
    Object.keys(autism).forEach(key => {
      autism[key] = Math.max(0, autism[key] - 0.01);
    });
  }

  /**
   * Update Bipolar indicators
   */
  updateBipolarIndicators(msg) {
    const elevated = this.currentState.bipolar.indicators.elevated;
    const depressed = this.currentState.bipolar.indicators.depressed;
    const alpha = 0.12;

    // ELEVATED PHASE INDICATORS

    // Grandiose plans: Big ambitious messages
    const grandioseWords = ['million', 'miljoen', 'revolutie', 'world', 'biggest', 'best ever', 'change everything'];
    if (grandioseWords.some(w => msg.topics.includes(w)) && msg.exclamationMarks > 1) {
      elevated.grandiosePlans = this.smooth(elevated.grandiosePlans, 1, alpha);
    }

    // Decreased sleep need: Very late productive sessions
    if ((msg.hour >= 2 && msg.hour < 5) && msg.wordCount > 100) {
      elevated.decreasedSleep = this.smooth(elevated.decreasedSleep, 1, alpha);
    }

    // Pressured speech: Long rapid messages
    if (msg.wordCount > 300 && msg.gapFromPrevious && msg.gapFromPrevious < 60) {
      elevated.pressuredSpeech = this.smooth(elevated.pressuredSpeech, 1, alpha);
    }

    // Irritability: Caps, profanity, frustration
    if (msg.caps > 2 || msg.hasProfanity) {
      elevated.irritability = this.smooth(elevated.irritability, 0.5, alpha);
    }

    // DEPRESSED PHASE INDICATORS

    // Low energy: Short messages, long gaps
    if (msg.wordCount < 10 && msg.gapFromPrevious && msg.gapFromPrevious > 3600) {
      depressed.lowEnergy = this.smooth(depressed.lowEnergy, 1, alpha);
    }

    // Hopelessness: Negative language
    const hopelessWords = ['hopeless', 'no point', 'give up', 'cant', 'never', 'worthless', 'pointless'];
    if (hopelessWords.some(w => msg.topics.includes(w))) {
      depressed.hopelessness = this.smooth(depressed.hopelessness, 1, alpha);
    }

    // Indecision: Seeking validation
    const indecisionWords = ['should i', 'what do you think', 'not sure', 'can\'t decide', 'help me choose'];
    if (indecisionWords.some(w => msg.topics.includes(w))) {
      depressed.indecision = this.smooth(depressed.indecision, 0.5, alpha);
    }

    // Withdrawal: Decreased frequency
    if (this.patternWindow.length >= 5) {
      const recentGaps = this.patternWindow.slice(-5).map(m => m.gapFromPrevious).filter(g => g);
      const avgGap = recentGaps.reduce((a, b) => a + b, 0) / (recentGaps.length || 1);
      if (avgGap > 7200) { // >2 hour average gaps
        depressed.withdrawal = this.smooth(depressed.withdrawal, 1, alpha);
      }
    }

    // Determine phase
    const elevatedScore = Object.values(elevated).reduce((a, b) => a + b, 0);
    const depressedScore = Object.values(depressed).reduce((a, b) => a + b, 0);
    
    if (elevatedScore > 2 && elevatedScore > depressedScore) {
      this.currentState.bipolar.currentPhase = 'elevated';
    } else if (depressedScore > 2 && depressedScore > elevatedScore) {
      this.currentState.bipolar.currentPhase = 'depressed';
    } else if (elevatedScore > 1 && depressedScore > 1) {
      this.currentState.bipolar.currentPhase = 'mixed';
    } else {
      this.currentState.bipolar.currentPhase = 'stable';
    }

    // Decay
    Object.keys(elevated).forEach(key => {
      elevated[key] = Math.max(0, elevated[key] - 0.01);
    });
    Object.keys(depressed).forEach(key => {
      depressed[key] = Math.max(0, depressed[key] - 0.01);
    });
  }

  /**
   * Update Psychosis safety indicators
   * CRITICAL: This is for SAFETY, not diagnosis
   */
  updatePsychosisIndicators(msg) {
    const psych = this.currentState.psychosis.indicators;
    const alpha = 0.1;

    // Disorganized thought: Incoherent, very long sentences, no structure
    if (msg.avgSentenceLength > 50 && !msg.hasList) {
      psych.disorganizedThought = this.smooth(psych.disorganizedThought, 0.5, alpha);
    }

    // Persecutory: Everyone against me language
    const persecutoryWords = ['they are watching', 'following me', 'against me', 'conspiracy', 'they know'];
    if (persecutoryWords.some(w => msg.topics.includes(w))) {
      psych.persecutory = this.smooth(psych.persecutory, 1, alpha);
    }

    // Grandiose delusions (different from bipolar - more extreme)
    const delusionalWords = ['chosen one', 'special powers', 'god told me', 'i am god', 'save humanity'];
    if (delusionalWords.some(w => msg.topics.includes(w))) {
      psych.grandiose = this.smooth(psych.grandiose, 1, alpha);
    }

    // Referential: Hidden meanings
    const referentialWords = ['hidden message', 'secret code', 'signs everywhere', 'meant for me'];
    if (referentialWords.some(w => msg.topics.includes(w))) {
      psych.referential = this.smooth(psych.referential, 1, alpha);
    }

    // Reality testing: Can distinguish (protective)
    const realityWords = ['i know this sounds', 'probably just', 'might be paranoid', 'rationally'];
    if (realityWords.some(w => msg.topics.includes(w))) {
      psych.realityTesting = this.smooth(psych.realityTesting, 1, alpha);
    }

    // Calculate concern level
    const concernScore = (psych.persecutory + psych.grandiose + psych.referential + psych.disorganizedThought) 
                        - (psych.realityTesting * 0.5);
    this.currentState.psychosis.concernLevel = Math.max(0, Math.min(100, concernScore * 25));

    // Decay
    Object.keys(psych).forEach(key => {
      psych[key] = Math.max(0, psych[key] - 0.02);
    });
  }

  /**
   * Update Neurotypical indicators
   */
  updateNeurotypicalIndicators(msg) {
    const nt = this.currentState.neurotypical.indicators;
    const alpha = 0.12;

    // Consistent pacing: Regular message lengths, normal timing
    if (this.patternWindow.length >= 5) {
      const lengths = this.patternWindow.map(m => m.wordCount);
      const lengthVariance = this.variance(lengths);
      if (lengthVariance < 500) {
        nt.consistentPacing = this.smooth(nt.consistentPacing, 1, alpha);
      }
    }

    // Linear thinking: Topics flow logically
    if (this.countTopicChanges() < 2 && this.patternWindow.length >= 3) {
      nt.linearThinking = this.smooth(nt.linearThinking, 1, alpha);
    }

    // Social norms: Standard politeness
    const politeWords = ['please', 'thank you', 'thanks', 'alsjeblieft', 'bedankt', 'sorry'];
    if (politeWords.some(w => msg.topics.includes(w))) {
      nt.socialNorms = this.smooth(nt.socialNorms, 0.5, alpha);
    }

    // Emotional regulation: Stable sentiment
    if (this.patternWindow.length >= 5) {
      const sentiments = this.patternWindow.map(m => m.sentiment);
      const allNeutral = sentiments.every(s => s === 'neutral');
      if (allNeutral) {
        nt.emotionalRegulation = this.smooth(nt.emotionalRegulation, 1, alpha);
      }
    }

    // Work-life balance: Normal hours (8-22)
    if (msg.hour >= 8 && msg.hour <= 22) {
      nt.workLifeBalance = this.smooth(nt.workLifeBalance, 0.3, alpha);
    }

    // Determine user type based on frequency
    const avgMessagesPerDay = this.calculateAverageMessagesPerDay();
    if (avgMessagesPerDay < 5) {
      this.currentState.neurotypical.userType = 'casual';
    } else if (avgMessagesPerDay < 20) {
      this.currentState.neurotypical.userType = 'regular';
    } else if (avgMessagesPerDay < 50) {
      this.currentState.neurotypical.userType = 'power';
    } else {
      this.currentState.neurotypical.userType = 'heavy';
    }

    // Decay
    Object.keys(nt).forEach(key => {
      nt[key] = Math.max(0, nt[key] - 0.01);
    });
  }

  /**
   * Update Anxiety indicators (cross-cutting)
   */
  updateAnxietyIndicators(msg) {
    const anx = this.currentState.anxiety.indicators;
    const alpha = 0.12;

    // Reassurance seeking
    const reassuranceWords = ['is this right', 'correct', 'am i doing', 'okay?', 'good?', 'sure?'];
    if (reassuranceWords.some(w => msg.topics.includes(w))) {
      anx.reassuranceSeeking = this.smooth(anx.reassuranceSeeking, 1, alpha);
    }

    // Catastrophizing
    const catastropheWords = ['worst', 'disaster', 'everything will', 'ruined', 'never recover'];
    if (catastropheWords.some(w => msg.topics.includes(w))) {
      anx.catastrophizing = this.smooth(anx.catastrophizing, 1, alpha);
    }

    // Perfectionism: Multiple revisions, endless questions
    if (msg.questionMarks > 3) {
      anx.perfectionism = this.smooth(anx.perfectionism, 0.5, alpha);
    }

    // Physical mentions
    const physicalWords = ['heart racing', 'can\'t breathe', 'shaking', 'sweating', 'panic'];
    if (physicalWords.some(w => msg.topics.includes(w))) {
      anx.physicalMentions = this.smooth(anx.physicalMentions, 1, alpha);
    }

    // Calculate score
    this.currentState.anxiety.score = Object.values(anx).reduce((a, b) => a + b, 0) * 20;

    // Decay
    Object.keys(anx).forEach(key => {
      anx[key] = Math.max(0, anx[key] - 0.01);
    });
  }

  // ==========================================
  // PROFILE CALCULATION
  // ==========================================

  /**
   * Calculate overall scores for each profile
   */
  calculateProfileScores() {
    // ADHD score
    const adhdIndicators = Object.values(this.currentState.adhd.indicators);
    this.currentState.adhd.score = Math.min(100, adhdIndicators.reduce((a, b) => a + b, 0) * 16);
    
    // Determine ADHD subtype
    const adhd = this.currentState.adhd.indicators;
    if (adhd.impulsivity + adhd.taskSwitching > adhd.paralysis + adhd.activationNeeded) {
      this.currentState.adhd.subtype = 'hyperactive';
    } else if (adhd.paralysis + adhd.activationNeeded > adhd.impulsivity + adhd.taskSwitching) {
      this.currentState.adhd.subtype = 'inattentive';
    } else if (adhdIndicators.reduce((a, b) => a + b, 0) > 2) {
      this.currentState.adhd.subtype = 'combined';
    }

    // Autism score
    const autismIndicators = Object.values(this.currentState.autism.indicators);
    this.currentState.autism.score = Math.min(100, autismIndicators.reduce((a, b) => a + b, 0) * 16);

    // Bipolar score
    const bipolarElevated = Object.values(this.currentState.bipolar.indicators.elevated);
    const bipolarDepressed = Object.values(this.currentState.bipolar.indicators.depressed);
    this.currentState.bipolar.score = Math.min(100, 
      (bipolarElevated.reduce((a, b) => a + b, 0) + bipolarDepressed.reduce((a, b) => a + b, 0)) * 10);

    // Neurotypical score
    const ntIndicators = Object.values(this.currentState.neurotypical.indicators);
    this.currentState.neurotypical.score = Math.min(100, ntIndicators.reduce((a, b) => a + b, 0) * 20);
  }

  /**
   * Determine primary profile
   */
  determinePrimaryProfile() {
    const scores = {
      neurotypical: this.currentState.neurotypical.score,
      adhd: this.currentState.adhd.score,
      autism: this.currentState.autism.score,
      bipolar: this.currentState.bipolar.score
    };

    // Find highest score
    let maxScore = 0;
    let primary = 'unknown';
    
    for (const [profile, score] of Object.entries(scores)) {
      if (score > maxScore) {
        maxScore = score;
        primary = profile;
      }
    }

    // Set confidence based on score differential
    const sortedScores = Object.values(scores).sort((a, b) => b - a);
    const differential = sortedScores[0] - (sortedScores[1] || 0);
    const confidence = Math.min(1, differential / 50);

    // Collect active traits
    const traits = [];
    if (this.currentState.adhd.score > 30) traits.push(`adhd-${this.currentState.adhd.subtype || 'traits'}`);
    if (this.currentState.autism.score > 30) traits.push('autism-traits');
    if (this.currentState.bipolar.score > 30) traits.push(`bipolar-${this.currentState.bipolar.currentPhase}`);
    if (this.currentState.anxiety.score > 30) traits.push('anxiety');
    if (this.currentState.neurotypical.score > 50) traits.push(`nt-${this.currentState.neurotypical.userType}`);

    this.currentState.profile = {
      primary: primary,
      confidence: confidence,
      traits: traits,
      lastUpdated: Date.now()
    };
  }

  // ==========================================
  // ADAPTATION OUTPUT
  // ==========================================

  /**
   * Get current adaptation instructions for AI
   */
  getCurrentAdaptation() {
    const state = this.currentState;
    let instructions = [];

    // Only adapt if we have enough confidence
    if (state.meta.totalAnalyzed < 5) {
      return {
        instructions: '[Profile building - use neutral defaults]',
        safetyFlags: [],
        profile: state.profile
      };
    }

    // SAFETY FIRST: Check psychosis concern level
    const safetyFlags = [];
    if (state.psychosis.concernLevel > 60) {
      safetyFlags.push('SAFETY_CHECK_NEEDED');
      instructions.push('⚠️ User may be in distress - be gentle, grounding, suggest professional support if appropriate');
    }

    // ADHD adaptations
    if (state.adhd.score > 40) {
      if (state.adhd.subtype === 'hyperactive') {
        instructions.push('Match energy, keep responses dynamic, use shorter sections');
      } else if (state.adhd.subtype === 'inattentive') {
        instructions.push('Provide structure, break into steps, offer to help start tasks');
      }
      if (state.adhd.indicators.hyperfocus > 0.5) {
        instructions.push('User in flow state - support deep dive, minimal interruption');
      }
      if (state.adhd.indicators.paralysis > 0.5) {
        instructions.push('User may be stuck - offer activation, first step help');
      }
    }

    // Autism adaptations
    if (state.autism.score > 40) {
      if (state.autism.indicators.literalLanguage > 0.5) {
        instructions.push('Be explicit and direct, avoid ambiguity');
      }
      if (state.autism.indicators.detailFocus > 0.5) {
        instructions.push('Provide thorough detail, precision appreciated');
      }
      if (state.autism.indicators.sensoryMentions > 0.3) {
        instructions.push('User sensitive to overwhelm - calm, structured responses');
      }
    }

    // Bipolar adaptations
    if (state.bipolar.score > 40) {
      if (state.bipolar.currentPhase === 'elevated') {
        instructions.push('Gently ground if needed, reality-check big plans supportively');
        if (state.bipolar.indicators.elevated.irritability > 0.5) {
          instructions.push('User may be irritable - stay calm, non-confrontational');
        }
      } else if (state.bipolar.currentPhase === 'depressed') {
        instructions.push('Extra supportive, validate feelings, avoid overwhelming with tasks');
      } else if (state.bipolar.currentPhase === 'mixed') {
        instructions.push('User in mixed state - stable, predictable responses');
        safetyFlags.push('MIXED_STATE_CARE');
      }
    }

    // Anxiety adaptations
    if (state.anxiety.score > 40) {
      instructions.push('Provide reassurance naturally, avoid adding pressure');
      if (state.anxiety.indicators.perfectionism > 0.5) {
        instructions.push('"Good enough" validation - help reduce perfectionism pressure');
      }
    }

    // Neurotypical adaptations
    if (state.neurotypical.score > 60 && state.profile.primary === 'neurotypical') {
      if (state.neurotypical.userType === 'casual') {
        instructions.push('Keep it simple and friendly');
      } else if (state.neurotypical.userType === 'power') {
        instructions.push('User is experienced - efficient, no hand-holding needed');
      } else if (state.neurotypical.userType === 'heavy') {
        instructions.push('Power user - match intensity, deep technical ok');
      }
    }

    return {
      instructions: instructions.length > 0 
        ? `[Adaptation: ${instructions.join(' | ')}]` 
        : null,
      safetyFlags: safetyFlags,
      profile: state.profile,
      scores: {
        adhd: state.adhd.score,
        autism: state.autism.score,
        bipolar: state.bipolar.score,
        neurotypical: state.neurotypical.score,
        anxiety: state.anxiety.score,
        psychosisConcern: state.psychosis.concernLevel
      }
    };
  }

  // ==========================================
  // HELPER FUNCTIONS
  // ==========================================

  smooth(current, target, alpha) {
    return current * (1 - alpha) + target * alpha;
  }

  variance(arr) {
    if (arr.length === 0) return 0;
    const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
    return arr.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / arr.length;
  }

  detectProfanity(text) {
    const words = ['fuck', 'shit', 'damn', 'kut', 'godverdomme', 'klote', 'kanker'];
    return words.some(w => text.toLowerCase().includes(w));
  }

  quickSentiment(text) {
    const lower = text.toLowerCase();
    const positive = ['good', 'great', 'thanks', 'perfect', 'love', 'nice', 'goed', 'mooi', 'top'];
    const negative = ['bad', 'wrong', 'hate', 'terrible', 'awful', 'slecht', 'kut', 'shit'];
    
    const posCount = positive.filter(w => lower.includes(w)).length;
    const negCount = negative.filter(w => lower.includes(w)).length;
    
    if (posCount > negCount) return 'positive';
    if (negCount > posCount) return 'negative';
    return 'neutral';
  }

  extractTopics(text) {
    return text.toLowerCase().replace(/[^\w\s]/g, ' ').split(/\s+/).filter(w => w.length > 2);
  }

  countTopicChanges() {
    if (this.patternWindow.length < 2) return 0;
    let changes = 0;
    for (let i = 1; i < this.patternWindow.length; i++) {
      const prev = new Set(this.patternWindow[i-1].topics);
      const curr = new Set(this.patternWindow[i].topics);
      const overlap = [...prev].filter(t => curr.has(t)).length;
      const total = new Set([...prev, ...curr]).size;
      if (total > 0 && overlap / total < 0.2) changes++;
    }
    return changes;
  }

  measureTopicRepetition() {
    if (this.patternWindow.length < 3) return 0;
    const allTopics = this.patternWindow.flatMap(m => m.topics);
    const topicCounts = {};
    allTopics.forEach(t => topicCounts[t] = (topicCounts[t] || 0) + 1);
    const maxCount = Math.max(...Object.values(topicCounts));
    return maxCount / this.patternWindow.length;
  }

  calculateAverageMessagesPerDay() {
    if (!this.currentState.meta.firstSeen) return 0;
    const daysSinceFirst = (Date.now() - this.currentState.meta.firstSeen) / (1000 * 60 * 60 * 24);
    if (daysSinceFirst < 0.1) return this.currentState.meta.totalAnalyzed; // Less than 2.4 hours
    return this.currentState.meta.totalAnalyzed / daysSinceFirst;
  }

  // ==========================================
  // PERSISTENCE
  // ==========================================

  loadState() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('[StateDetection] Could not load state:', e);
    }
    return this.getDefaultState();
  }

  saveState() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.currentState));
    } catch (e) {
      console.warn('[StateDetection] Could not save state:', e);
    }
  }

  /**
   * Get full state for debugging/display
   */
  getFullState() {
    return this.currentState;
  }

  /**
   * Reset all state
   */
  reset() {
    this.currentState = this.getDefaultState();
    this.patternWindow = [];
    this.sessionData = [];
    localStorage.removeItem(this.storageKey);
  }
}

// Singleton
const stateDetection = new StateDetection();

export default stateDetection;
